<template>
    <div v-if="featureKey" class="sp-cs-interest" :class="{ 'has-feedback': allowFeedback }">
        <template v-if="!locked">
            <p class="sp-cs-interest__prompt">{{ prompt }}</p>
            <div class="sp-cs-interest__stars" role="radiogroup" :aria-label="prompt">
                <button
                    v-for="value in 5"
                    :key="`star-${value}`"
                    type="button"
                    class="sp-cs-interest__star"
                    role="radio"
                    :aria-checked="selected === value ? 'true' : 'false'"
                    :aria-label="`${value} star${value === 1 ? '' : 's'}`"
                    :disabled="saving || loading"
                    @mouseenter="hover = value"
                    @mouseleave="hover = 0"
                    @focus="hover = value"
                    @blur="hover = 0"
                    @click="select(value)"
                >
                    <font-awesome-icon
                        :icon="displayValue >= value ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                        class="h-6 w-6"
                        :class="displayValue >= value ? 'is-on' : 'is-off'"
                    />
                </button>
            </div>
            <div v-if="allowFeedback" class="sp-cs-interest__feedback">
                <div class="sp-cs-interest__feedback-head">
                    <label class="sp-cs-interest__feedback-label" :for="feedbackId">Leave feedback</label>
                    <span class="sp-cs-interest__feedback-note">Optional</span>
                </div>
                <StatePortalCommunityComposer
                    ref="feedbackComposer"
                    :input-id="feedbackId"
                    v-model="feedback"
                    :disabled="saving || loading"
                    :can-send="Boolean(selected) && !saving && !loading"
                    :sending="saving"
                    placeholder="Optional — tell us what’s working, what’s missing, or what you’d want next."
                    @update:attachments="attachments = $event"
                    @send="submitWithFeedback"
                />
            </div>
            <p v-if="error" class="sp-cs-interest__error">Couldn’t save — we’ll keep trying next time.</p>
        </template>
        <div v-else class="sp-cs-interest__locked">
            <div class="sp-cs-interest__stars" aria-hidden="true">
                <span
                    v-for="value in 5"
                    :key="`locked-star-${value}`"
                    class="sp-cs-interest__star is-static"
                >
                    <font-awesome-icon
                        :icon="selected >= value ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                        class="h-5 w-5"
                        :class="selected >= value ? 'is-on' : 'is-off'"
                    />
                </span>
            </div>
            <p class="sp-cs-interest__thanks">
                {{ thanksCopy }}
            </p>
        </div>
    </div>
</template>

<script>
import useMainStore from '@/stores/main';
import StatePortalCommunityComposer from '@/components/Instructor/StatePortal/StatePortalCommunityComposer.vue';
import {
    getFeatureInterestForUser,
    recordFeatureInterest,
} from '@/components/Instructor/StatePortal/statePortalAnalytics.js';

let interestSeq = 0;

export default {
    name: 'StatePortalComingSoonInterest',
    components: {
        StatePortalCommunityComposer,
    },
    props: {
        featureKey: {
            type: String,
            default: '',
        },
        featureLabel: {
            type: String,
            default: '',
        },
        surface: {
            type: String,
            default: 'state-portal-coming-soon',
        },
        prompt: {
            type: String,
            default: 'How useful would this be for you?',
        },
        allowFeedback: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        interestSeq += 1;
        return {
            selected: 0,
            hover: 0,
            saving: false,
            loading: false,
            locked: false,
            error: false,
            feedback: '',
            attachments: [],
            submittedFeedback: '',
            submittedAttachments: [],
            uid: interestSeq,
        };
    },
    computed: {
        displayValue() {
            return this.hover || this.selected;
        },
        feedbackId() {
            return `sp-cs-interest-feedback-${this.uid}`;
        },
        thanksCopy() {
            const hasFiles = (this.submittedAttachments || []).length > 0;
            if (this.submittedFeedback && hasFiles) {
                return `You rated this ${this.selected} / 5. Thanks — we’ve got your vote, feedback, and attachments for this portal.`;
            }
            if (this.submittedFeedback) {
                return `You rated this ${this.selected} / 5. Thanks — we’ve got your vote and feedback for this portal.`;
            }
            if (hasFiles) {
                return `You rated this ${this.selected} / 5. Thanks — we’ve got your vote and attachments for this portal.`;
            }
            return `You rated this ${this.selected} / 5. Thanks — we’ve got your vote for this portal.`;
        },
        mainStore() {
            return useMainStore();
        },
        portalState() {
            return String(
                this.mainStore.currentStatePortal?.abbreviation
                || this.mainStore.user?.currentStatePortal
                || '',
            ).trim().toUpperCase();
        },
    },
    watch: {
        featureKey: {
            immediate: true,
            handler() {
                this.loadExistingRating();
            },
        },
        portalState() {
            this.loadExistingRating();
        },
    },
    methods: {
        reset() {
            this.selected = 0;
            this.hover = 0;
            this.saving = false;
            this.loading = false;
            this.locked = false;
            this.error = false;
            this.feedback = '';
            this.attachments = [];
            this.submittedFeedback = '';
            this.submittedAttachments = [];
            this.$refs.feedbackComposer?.reset?.();
        },
        applyExisting(entry = null) {
            const rating = Number(entry?.rating);
            if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
                this.locked = false;
                return;
            }
            this.selected = rating;
            this.hover = 0;
            this.locked = true;
            this.error = false;
            this.submittedFeedback = String(entry?.feedback || '').trim();
            this.submittedAttachments = Array.isArray(entry?.attachments) ? entry.attachments : [];
        },
        async loadExistingRating() {
            const key = String(this.featureKey || '').trim();
            this.reset();
            if (!key) return;
            this.loading = true;
            const result = await getFeatureInterestForUser({
                mainStore: this.mainStore,
                feature: key,
                state: this.portalState,
            });
            this.loading = false;
            if (result?.rated && result?.entry) {
                this.applyExisting(result.entry);
            }
        },
        async persistRating(value, message = '', files = []) {
            if (this.saving || this.locked || this.loading) return;
            this.selected = value;
            this.saving = true;
            this.error = false;
            const result = await recordFeatureInterest({
                mainStore: this.mainStore,
                feature: this.featureKey,
                featureLabel: this.featureLabel || this.featureKey,
                surface: this.surface,
                rating: value,
                feedback: message,
                attachments: files,
            });
            this.saving = false;
            if (result?.recorded || result?.alreadyRated) {
                this.applyExisting(result.entry || { rating: value, feedback: message, attachments: files });
            } else if (!result?.skipped) {
                this.error = true;
            }
        },
        async select(value) {
            if (this.allowFeedback) {
                if (this.saving || this.locked || this.loading) return;
                this.selected = value;
                this.error = false;
                return;
            }
            await this.persistRating(value);
        },
        async submitWithFeedback(payload = {}) {
            if (!this.selected) return;
            const message = payload?.text != null ? payload.text : this.feedback;
            const files = Array.isArray(payload?.attachments) ? payload.attachments : this.attachments;
            await this.persistRating(this.selected, message, files);
        },
    },
};
</script>

<style scoped>
.sp-cs-interest {
    margin-top: 1rem;
}

.sp-cs-interest.has-feedback {
    text-align: left;
}

.sp-cs-interest__prompt {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
}

.sp-cs-interest.has-feedback .sp-cs-interest__prompt {
    text-align: center;
}

.sp-cs-interest__stars {
    margin-top: 0.7rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.15rem;
}

.sp-cs-interest.has-feedback .sp-cs-interest__stars {
    display: flex;
    width: 100%;
}

.sp-cs-interest__star {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.35rem;
    height: 2.35rem;
    border: 0;
    border-radius: 0.7rem;
    background: transparent;
    cursor: pointer;
    transition: transform 120ms ease, background-color 150ms ease;
}

.sp-cs-interest__star.is-static {
    cursor: default;
}

.sp-cs-interest__star:hover:not(:disabled):not(.is-static) {
    transform: translateY(-1px) scale(1.08);
    background: #fffbeb;
}

.sp-cs-interest__star:disabled {
    cursor: default;
}

.sp-cs-interest__star :deep(.is-on) {
    color: #f59e0b;
}

.sp-cs-interest__star :deep(.is-off) {
    color: #cbd5e1;
}

.sp-cs-interest__feedback {
    margin-top: 1.05rem;
}

.sp-cs-interest__feedback-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.45rem;
}

.sp-cs-interest__feedback-label {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0f766e;
}

.sp-cs-interest__feedback-note {
    font-size: 0.68rem;
    font-weight: 600;
    color: #94a3b8;
}

.sp-cs-interest__locked {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.sp-cs-interest__thanks {
    margin: 0.45rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: #0f766e;
    font-weight: 600;
    text-align: center;
}

.sp-cs-interest__error {
    margin: 0.45rem 0 0;
    font-size: 0.75rem;
    color: #b45309;
    text-align: center;
}
</style>
